(() => {
  function selectActiveGuide(guidePositions, activationLine, isAtPageBottom, navigationTargetId = null) {
    const visibleGuides = guidePositions.filter((guide) => !guide.hidden);

    if (visibleGuides.length === 0) return null;
    if (navigationTargetId && visibleGuides.some((guide) => guide.id === navigationTargetId)) {
      return navigationTargetId;
    }
    if (isAtPageBottom) return visibleGuides[visibleGuides.length - 1].id;

    return visibleGuides.reduce((activeId, guide) => {
      return guide.top <= activationLine ? guide.id : activeId;
    }, visibleGuides[0].id);
  }

  function getMobileToolbarState(isStuck, activeGuideTitle, defaultLabel) {
    return { label: isStuck && activeGuideTitle ? activeGuideTitle : defaultLabel };
  }

  function getGuideActivationAnchor(guide) {
    return guide?.querySelector('[data-guide-activation-anchor]') || guide || null;
  }

  function getGuideNavigationItems(guides) {
    return guides
      .map((guide) => ({
        id: guide.id || '',
        title: (guide.title || '').trim(),
      }))
      .filter((guide) => guide.id && guide.title);
  }

  if (typeof window !== 'undefined' && typeof document !== 'undefined' && typeof customElements !== 'undefined') {
    class UserGuideCatalog extends HTMLElement {
      connectedCallback() {
        if (this.dataset.initialized === 'true') return;

        this.dataset.initialized = 'true';
        this.guides = Array.from(this.querySelectorAll('[data-guide]'));
        this.buildNavigation();
        this.navLinks = Array.from(this.querySelectorAll('[data-guide-nav-link]'));
        this.toolbar = this.querySelector('[data-guide-toolbar]');
        this.toolbarSentinel = this.querySelector('[data-guide-toolbar-sentinel]');
        this.drawer = this.querySelector('[data-guide-drawer]');
        this.drawerTrigger = this.querySelector('[data-guide-drawer-open]');
        this.drawerTriggerLabel = this.querySelector('[data-guide-drawer-label]');
        this.defaultLabel = this.drawerTriggerLabel?.dataset.defaultLabel || '';
        this.abortController = new AbortController();
        this.guideFrame = null;
        this.navigationTargetId = null;
        this.navigationReleaseTimer = null;
        this.activeGuideId = null;
        this.isToolbarStuck = false;

        this.bindEvents();
        this.syncActiveGuide();
      }

      buildNavigation() {
        const navigationItems = getGuideNavigationItems(
          this.guides.map((guide) => ({ id: guide.id, title: guide.dataset.guideTitle }))
        );

        this.querySelectorAll('[data-guide-nav-list]').forEach((list) => {
          const linkClass =
            list.dataset.guideNavList === 'drawer'
              ? 'user-guide-catalog__drawer-link'
              : 'user-guide-catalog__nav-link';
          const fragment = document.createDocumentFragment();

          navigationItems.forEach((item, index) => {
            const listItem = document.createElement('li');
            const link = document.createElement('a');

            link.className = `${linkClass}${index === 0 ? ' is-active' : ''}`;
            link.href = `#${item.id}`;
            link.dataset.guideNavLink = '';
            link.dataset.target = item.id;
            link.textContent = item.title;
            if (index === 0) link.setAttribute('aria-current', 'true');

            listItem.append(link);
            fragment.append(listItem);
          });

          list.replaceChildren(fragment);
        });
      }

      disconnectedCallback() {
        window.clearTimeout(this.navigationReleaseTimer);
        if (this.guideFrame !== null) window.cancelAnimationFrame(this.guideFrame);
        this.abortController?.abort();

        if (this.drawer?.open) this.drawer.close();
        document.body.classList.remove('user-guide-catalog--drawer-open');
        delete this.dataset.initialized;
      }

      bindEvents() {
        const { signal } = this.abortController;
        const handleViewportChange = () => {
          this.queueGuideSync();
          this.scheduleNavigationRelease();
        };

        window.addEventListener('scroll', handleViewportChange, { passive: true, signal });
        window.addEventListener('resize', handleViewportChange, { passive: true, signal });

        this.navLinks.forEach((link) => {
          link.addEventListener('click', (event) => this.navigateToGuide(event), { signal });
        });

        this.drawerTrigger?.addEventListener('click', () => this.openDrawer(), { signal });
        this.querySelectorAll('[data-guide-drawer-close]').forEach((button) => {
          button.addEventListener('click', () => this.closeDrawer(), { signal });
        });

        this.drawer?.addEventListener(
          'click',
          (event) => {
            if (event.target === this.drawer) this.closeDrawer();
          },
          { signal }
        );
        this.drawer?.addEventListener(
          'close',
          () => {
            document.body.classList.remove('user-guide-catalog--drawer-open');
            this.drawerTrigger?.setAttribute('aria-expanded', 'false');
            this.drawerTrigger?.focus();
          },
          { signal }
        );
      }

      queueGuideSync() {
        if (this.guideFrame !== null) return;

        this.guideFrame = window.requestAnimationFrame(() => {
          this.guideFrame = null;
          this.syncActiveGuide();
        });
      }

      syncActiveGuide() {
        this.syncToolbarState();
        const guidePositions = this.guides.map((guide) => {
          const activationAnchor = getGuideActivationAnchor(guide);

          return {
            id: guide.id,
            top: activationAnchor.getBoundingClientRect().top,
            hidden: guide.hidden,
          };
        });
        const isAtPageBottom =
          Math.ceil(window.scrollY + window.innerHeight) >= document.documentElement.scrollHeight - 2;

        this.setActiveGuide(
          selectActiveGuide(guidePositions, this.getActivationLine(), isAtPageBottom, this.navigationTargetId)
        );
      }

      getVisibleHeaderBottom() {
        const stickyHeader = document.querySelector('.section-header');
        const stickyHeaderBottom = stickyHeader?.getBoundingClientRect().bottom;
        const configuredHeaderHeight =
          Number.parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 0;

        return Number.isFinite(stickyHeaderBottom) ? Math.max(0, stickyHeaderBottom) : configuredHeaderHeight;
      }

      syncToolbarState() {
        const visibleHeaderBottom = this.getVisibleHeaderBottom();
        this.style.setProperty('--user-guide-sticky-top', `${visibleHeaderBottom}px`);

        if (!this.toolbar || !this.toolbarSentinel) return;

        const isMobile = window.matchMedia('(max-width: 989px)').matches;
        const sentinelTop = this.toolbarSentinel.getBoundingClientRect().top;
        this.isToolbarStuck = isMobile && sentinelTop <= visibleHeaderBottom;
        this.toolbar.classList.toggle('is-stuck', this.isToolbarStuck);
        this.style.setProperty('--user-guide-toolbar-height', `${this.toolbar.offsetHeight}px`);
        this.updateMobileToolbar();
      }

      getActivationLine() {
        const toolbarHeight = this.isToolbarStuck ? this.toolbar?.offsetHeight || 0 : 0;
        return this.getVisibleHeaderBottom() + toolbarHeight + 24;
      }

      setActiveGuide(guideId) {
        this.activeGuideId = guideId;
        this.navLinks.forEach((link) => {
          const isActive = Boolean(guideId) && link.dataset.target === guideId;
          link.classList.toggle('is-active', isActive);

          if (isActive) {
            link.setAttribute('aria-current', 'true');
          } else {
            link.removeAttribute('aria-current');
          }
        });
        this.updateMobileToolbar();
      }

      updateMobileToolbar() {
        if (!this.drawerTriggerLabel) return;

        const activeGuideTitle =
          this.guides.find((guide) => guide.id === this.activeGuideId)?.dataset.guideTitle || '';
        const toolbarState = getMobileToolbarState(this.isToolbarStuck, activeGuideTitle, this.defaultLabel);
        this.drawerTriggerLabel.textContent = toolbarState.label;
      }

      navigateToGuide(event) {
        const link = event.currentTarget;
        const target = this.guides.find((guide) => guide.id === link.dataset.target);
        if (!target) return;

        event.preventDefault();
        this.closeDrawer();
        this.navigationTargetId = target.id;
        this.scheduleNavigationRelease();
        this.setActiveGuide(target.id);
        history.pushState(null, '', `#${target.id}`);
        const scrollTarget = getGuideActivationAnchor(target);
        scrollTarget.scrollIntoView({
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
          block: 'start',
        });
      }

      scheduleNavigationRelease() {
        if (!this.navigationTargetId) return;

        window.clearTimeout(this.navigationReleaseTimer);
        this.navigationReleaseTimer = window.setTimeout(() => {
          this.navigationTargetId = null;
          this.navigationReleaseTimer = null;
        }, 250);
      }

      openDrawer() {
        if (!this.drawer || typeof this.drawer.showModal !== 'function') return;

        this.drawer.showModal();
        this.drawerTrigger?.setAttribute('aria-expanded', 'true');
        document.body.classList.add('user-guide-catalog--drawer-open');
        this.drawer.querySelector('[data-guide-drawer-close]')?.focus();
      }

      closeDrawer() {
        if (this.drawer?.open) this.drawer.close();
      }
    }

    if (!customElements.get('user-guide-catalog')) {
      customElements.define('user-guide-catalog', UserGuideCatalog);
    }
  }

  if (typeof module !== 'undefined') {
    module.exports = { selectActiveGuide, getMobileToolbarState, getGuideActivationAnchor, getGuideNavigationItems };
  }
})();
